/**
 * «Долг» в записи операции (макет 14.10.2026): что гасится и как.
 *
 * Новых действий здесь нет — только выбор из существующих, чтобы человек не
 * искал их по трём экранам:
 *
 * · кредитка — перевод на карту, как «Записать погашение»;
 * · кредит, сумма в пределах платежа по графику — оплата этого платежа, как
 *   «Оплачено» в «Платежах»: остаток долга уменьшается сам;
 * · кредит, сверх платежа — излишек досрочно, как «Досрочно» в «Платежах»;
 * · платёж за срок уже отмечен или графика нет (личный долг) — вся сумма
 *   досрочно: целиком в тело долга.
 *
 * Что именно будет, говорится одной строкой до нажатия.
 */
import {
  ZERO, addDays, formatMoney, loanBalance, paymentInstances, sub, unsettledInstances,
  type Account, type Loan, type LoanFact, type Money, type PaymentInstance, type PlainDate,
} from '@skoka/core';
import type { AppState } from './store.js';
import { dayMonth } from './text.js';

/** Платёж по графику близко к дате записи — тот, который человек и платит. */
const AHEAD_DAYS = 14;

export interface DebtOption {
  /** `card:<счёт карты>` или `loan:<долг>`. */
  id: string;
  kind: 'card' | 'loan';
  name: string;
  /** Под названием в карточке «Куда»: «Долг 152 400 ₽», «осталось 3 240 000 ₽». */
  left: string;
  /** Под названием в списке: ближайший платёж или остаток. */
  line: string;
  account?: Account;
  loan?: Loan;
  /** Ближайший неоплаченный платёж по графику. */
  due: PaymentInstance | null;
  /** Сумма по умолчанию: платёж по графику, у карты — весь долг. */
  suggested: Money;
}

type State = Pick<AppState, 'accounts' | 'loans' | 'obligations' | 'reserves' | 'today' | 'trackingStartedAt'>;

/**
 * Ближайший неоплаченный платёж долга: просроченный или до двух недель вперёд.
 *
 * Прошлые берутся только у `unsettledInstances`: он знает, с какого дня
 * приложение ведёт учёт и с какого платёж существует. Свой взгляд назад
 * находил «платёж за сентябрь» у кредита, заведённого в октябре.
 */
export function loanDue(state: State, loan: Loan, date: PlainDate): PaymentInstance | null {
  if (!loan.obligationId) return null;
  const mine = state.obligations.filter((o) => o.id === loan.obligationId);
  const late = unsettledInstances({
    obligations: mine, reserves: state.reserves, today: state.today,
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
  });
  const soon = paymentInstances({
    obligations: mine, reserves: state.reserves, today: state.today,
    from: addDays(state.today, 1), to: addDays(date > state.today ? date : state.today, AHEAD_DAYS),
  }).filter((i) => !i.settled);
  return [...late, ...soon].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))[0] ?? null;
}

/** Долги, которые можно погасить: кредитки и кредиты с остатком. Сначала — те, где ждут платёж. */
export function debtOptions(state: State, date: PlainDate): DebtOption[] {
  const loans = state.loans
    .filter((l) => l.kind !== 'credit_card' && l.principal > ZERO)
    .map((loan): DebtOption => {
      const due = loanDue(state, loan, date);
      return {
        id: `loan:${loan.id}`, kind: 'loan', name: loan.name, loan, due,
        left: `осталось ${formatMoney(loan.principal)}`,
        line: due ? `платёж ${formatMoney(due.unpaid)} · ${dayMonth(due.scheduledDate, state.today)}` : `осталось ${formatMoney(loan.principal)}`,
        suggested: due ? due.unpaid : loan.payment > ZERO ? loan.payment : ZERO,
      };
    });
  const cards = state.accounts
    .filter((a) => a.kind === 'credit_card' && !a.archived)
    .map((account): DebtOption => {
      const debt = account.balance < ZERO ? sub(ZERO, account.balance) : ZERO;
      const text = debt > ZERO ? `Долг ${formatMoney(debt)}` : 'Долга нет';
      return { id: `card:${account.id}`, kind: 'card', name: account.name, account, due: null, left: text, line: text, suggested: debt };
    });
  const order = (o: DebtOption): number => (o.due ? 0 : o.kind === 'card' && o.suggested > ZERO ? 1 : 2);
  return [...loans, ...cards].sort((a, b) => order(a) - order(b)
    || (a.due && b.due ? a.due.scheduledDate.localeCompare(b.due.scheduledDate) : 0));
}

export interface DebtSplit {
  /** Оплата платежа по графику. */
  payment: Money;
  /** Досрочно — целиком в тело долга. */
  extra: Money;
  /** Что будет, одной строкой — до нажатия. */
  note: string;
  /** Записать нельзя: почему. */
  blocked: string | null;
}

/** Как сумма ляжет на долг. */
export function splitDebt(option: DebtOption, amount: Money, date: PlainDate, today: PlainDate): DebtSplit {
  if (option.kind === 'card') {
    const debt = option.suggested;
    const rest = sub(debt, amount);
    return {
      payment: amount, extra: ZERO, blocked: null,
      note: amount <= ZERO ? 'Перевод на карту: долг по ней уменьшится на эту сумму.'
        : rest > ZERO ? `Долг по карте станет ${formatMoney(rest)}.`
        : rest === ZERO ? 'Долг по карте закроется.'
        : `Долг по карте закроется, ${formatMoney(sub(ZERO, rest))} останется на карте.`,
    };
  }
  const loan = option.loan!;
  const due = option.due;
  const payment = due ? (amount < due.unpaid ? amount : due.unpaid) : ZERO;
  const extra = sub(amount, payment);
  const when = due ? dayMonth(due.scheduledDate, today) : '';
  if (extra > ZERO && !loan.allowsEarly) {
    return {
      payment, extra, note: '',
      blocked: due
        ? `По договору досрочно гасить нельзя — впиши не больше ${formatMoney(due.unpaid)}.`
        : 'По договору досрочно гасить нельзя, а платёж по графику уже отмечен.',
    };
  }
  const after = remainingAfter(loan, payment, extra, date);
  const left = `остаток долга станет ≈ ${formatMoney(after)}`;
  let note: string;
  if (amount <= ZERO) note = due ? `Платёж за ${when} — ${formatMoney(due.unpaid)}.` : 'Сумма уйдёт в погашение долга.';
  else if (due && extra === ZERO && payment === due.unpaid) note = `Платёж за ${when} — в «Платежах» отмечу «Оплачено», остаток долга уменьшится сам.`;
  else if (due && extra === ZERO) note = `Часть платежа за ${when}: в «Платежах» останется ${formatMoney(sub(due.unpaid, payment))}.`;
  else if (due) note = `${formatMoney(payment)} — платёж за ${when}, ${formatMoney(extra)} — досрочно: ${left}.`;
  else if (loan.obligationId) note = `Ближайший платёж уже отмечен — вся сумма пойдёт досрочно: ${left}.`;
  else note = `Вся сумма — в погашение: ${left}.`;
  return { payment, extra, note, blocked: null };
}

/**
 * Остаток после записи — тем же расчётом, что и в «Долгах»: платёж по
 * графику сначала гасит набежавшие по дням проценты, досрочное — целиком в тело.
 */
function remainingAfter(loan: Loan, payment: Money, extra: Money, date: PlainDate): Money {
  const facts: LoanFact[] = [];
  if (payment > ZERO) facts.push({ date, amount: payment, kind: 'payment' });
  if (extra > ZERO) facts.push({ date, amount: extra, kind: 'extra' });
  return loanBalance({ ...loan, principalAsOf: addDays(date, -1) }, facts, date);
}
